// Entry module: it mounts the tree and intentionally exports nothing, so the
// fast-refresh rule about co-located components cannot be satisfied here.
/* eslint-disable react-refresh/only-export-components */
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import { StrictMode, useCallback, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { PostHogErrorBoundary, PostHogProvider } from 'posthog-js/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { App } from './App.tsx';
import { getPostHogClient } from './analytics.ts';
import { GrampsAuthDialog } from './components/GrampsAuthDialog.tsx';
import { updateTitleFromCurrentTree } from './siteTitle.ts';

// The backend runs on 2 CPUs and the ancestor queries are rule-evaluated server side
// (~600 ms each regardless of result size), so a cached answer must not trigger a
// background refetch: that CPU is needed by the requests actually blocking the UI.
// Pressing "r" still forces a full refresh via queryClient.invalidateQueries().
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

const theme = createTheme({
  palette: {
    primary: {
      main: '#d97706',
    },
  },
})

function AppErrorFallback() {
  return (
    <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <Typography variant="body2" color="text.secondary" align="center">
        Une erreur est survenue. Elle a été enregistrée et sera examinée.
      </Typography>
    </Box>
  )
}

function GrampsAuthGate() {
  const [authDialogOpen, setAuthDialogOpen] = useState(false);

  const handleAuthRequired = useCallback(() => {
    setAuthDialogOpen(true);
  }, []);

  const handleAuthenticated = useCallback(() => {
    setAuthDialogOpen(false);
    // Anything fetched before logging in was cached unauthenticated (typically empty),
    // and staleTime would otherwise keep serving it for five minutes.
    void queryClient.invalidateQueries();
    void updateTitleFromCurrentTree();
  }, []);

  return (
    <>
      <App onAuthRequired={handleAuthRequired} />
      <GrampsAuthDialog open={authDialogOpen} onAuthenticated={handleAuthenticated} />
    </>
  );
}

async function bootstrap() {
  const posthogClient = getPostHogClient();

  if (!posthogClient) {
    throw new Error('Missing PostHog client configuration. Set VITE_POSTHOG_KEY to enable error reporting.');
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ThemeProvider theme={theme}>
        <PostHogProvider client={posthogClient}>
          <PostHogErrorBoundary fallback={<AppErrorFallback />}>
            <QueryClientProvider client={queryClient}>
              <GrampsAuthGate />
            </QueryClientProvider>
          </PostHogErrorBoundary>
        </PostHogProvider>
      </ThemeProvider>
    </StrictMode>,
  )
}

void bootstrap();
